import { Box, Button, Divider, IconButton, Stack, SvgIcon, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import download from 'downloadjs';
import { isArray } from 'lodash';
import { ElementType } from 'react';
import { Check, FileDownload02, Trash01, UploadCloud01 } from 'untitledui-js-base';
import { DocumentIcon } from '../documents';
import { DocumentType } from '../types';
import { MediaValue } from '../types/accident';
import { LineClamp } from './LineClamp';

interface DocumentValueItemProps {
  document: MediaValue;
  onRemove?: () => void;
}

export const DocumentValueItem = ({
  document: { url, createdDate, fileType, fileName },
  onRemove
}: DocumentValueItemProps) => (
  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" sx={{ fontSize: '12px', px: 3 }}>
    <Stack direction="row" alignItems="center" gap={1}>
      <DocumentIcon mimeType={fileType} />
      <Typography
        component="a"
        href={url}
        target="_blank"
        fontSize="14px"
        sx={{
          textDecoration: 'none',
          ' &:hover': { textDecoration: 'underline' }
        }}
      >
        {fileName}
      </Typography>
    </Stack>
    <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between">
      <Typography fontSize="12px">{format(new Date(createdDate), 'dd.MM.yyyy')}</Typography>
      <Stack direction="row" justifyContent="right">
        <Tooltip title="Stáhnout soubor">
          <IconButton disabled={!url} onClick={() => download(url)} color="info">
            <FileDownload02 size="16px" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Odstranit soubor">
          <IconButton disabled={!url} onClick={onRemove} color="error">
            <Trash01 size="16px" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  </Stack>
);

export const BasicDocumentValueItem = ({
  url,
  fileName,
  createdDate,
  component = 'a'
}: {
  url: string;
  fileName: string;
  createdDate?: string;
  component?: ElementType;
}) => (
  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" sx={{ fontSize: '12px' }}>
    <Stack direction="row" alignItems="center" gap={1}>
      <DocumentIcon mimeType={DocumentType.PDF} />
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={{ md: 1 }}>
        <Typography component={component} href={url} target="_blank" fontSize="14px" color="primary.main">
          {fileName}
        </Typography>
        {createdDate && (
          <Typography fontSize="12px" color="gray">
            ({format(createdDate, 'dd.MM.yyyy HH:mm')})
          </Typography>
        )}
      </Stack>
    </Stack>
  </Stack>
);

interface DocumentItemProps {
  title: string;
  limit: number;
  onClick?: () => void;
  onRemove?: (id: number) => void;
  values?: MediaValue | MediaValue[];
  uploadNecessary?: boolean;
  uploadAvailable?: boolean;
}

export const DocumentItem = ({
  title,
  limit,
  values,
  onRemove,
  onClick,
  uploadAvailable = true
}: DocumentItemProps) => (
  <>
    <Stack>
      <Stack direction="row" gap={2} alignItems="center">
        {values && (
          <SvgIcon color="success">
            <Check />
          </SvgIcon>
        )}
        <LineClamp maxLines={1} fontWeight="bold">
          {title}
        </LineClamp>
      </Stack>
      <Box mt={1}>
        <Divider />
      </Box>
      <Stack gap={1} mt={1}>
        {isArray(values) ? (
          <Stack gap={1}>
            {values.map((value, index) => (
              <DocumentValueItem key={index} document={value} onRemove={() => onRemove?.(value.id)} />
            ))}
          </Stack>
        ) : (
          values && <DocumentValueItem document={values} onRemove={() => onRemove?.(values.id)} />
        )}
      </Stack>
      {(limit === 0 || (isArray(values) && values.length < limit)) && uploadAvailable && (
        <Box mt={2}>
          <Button
            variant="text"
            color="success"
            size="medium"
            endIcon={<UploadCloud01 size="16px" />}
            onClick={onClick}
            sx={{
              width: 1,
              border: 'dashed 1px',
              borderColor: 'success.main'
            }}
          >
            Nahrát další
          </Button>
        </Box>
      )}
    </Stack>
    <Divider />
  </>
);

export const DocumentItemToUpload = ({ title, onClick, uploadNecessary }: DocumentItemProps) => (
  <Stack
    direction={{ xs: 'column', md: 'row' }}
    justifyContent="space-between"
    sx={{
      paddingX: '1rem',
      paddingY: '.3rem',
      border: '1px solid',
      borderColor: uploadNecessary ? 'error.main' : 'grey.300',
      borderRadius: '3px',
      '&:hover': {
        cursor: 'pointer'
      }
    }}
    onClick={onClick}
  >
    <Stack direction="row" gap={2} alignItems="center">
      <LineClamp maxLines={1} fontWeight="bold">
        {title}
      </LineClamp>
    </Stack>
    <Stack direction="row" gap={2} alignItems="center">
      <Button variant="text" color="success" size="small" endIcon={<UploadCloud01 size="16px" />}>
        Nahrát dokument
      </Button>
    </Stack>
  </Stack>
);
